<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading :style="{ display: loaded.container && loaded.form ? 'none' : 'block' }"></Loading>
        <!-- LOADED -->
        <div :style="{ display: loaded.container && loaded.form ? 'initial' : 'none' }">
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>
                    {{ $t('edit_drugsafe_user') }}: {{ drugSafeUserOriginal.first_name }}
                    {{ drugSafeUserOriginal.last_name }}
                    {{ drugSafeUserOriginal.role ? ` (${$t(`role_${drugSafeUserOriginal.role}`)})` : '' }}
                </h3>
            </div>
            <!-- EDIT DRUGSAFE USER FORM -->
            <div class="row mt-4">
                <div class="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                    <ValidationObserver ref="editSafeUserForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(handleSubmitClick)">
                            <!-- FORM FIELDS -->
                            <DrugSafeUserFormFields
                                :createWebUser="false"
                                :edit="true"
                                @loaded="setLoaded($event)"
                                :drugSafeUser="drugSafeUser"
                            />
                            <!-- BUTTONS: SUBMIT, CANCEL -->
                            <div class="d-flex flex-row mt-2">
                                <div class="d-flex flex-column mr-3">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        :value="$t('save')"
                                        :disabled="submitClicked || invalid"
                                    />
                                </div>
                                <div class="d-flex flex-column">
                                    <button
                                        type="button"
                                        class="btn btn-md btn-secondary"
                                        @click="returnToPreviousPage"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('cancel') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO ASSOCIATE SAFE USER WITH WEB USER? -->
        <b-modal id="submitConfirmationModal" centered :title="$t('set_web_user_are_you_sure_title')" hide-footer>
            <!-- MODAL CONTENT -->
            <div class="row">
                <div class="col">
                    <p>{{ $t('set_web_user_are_you_sure') }}</p>
                    <p>
                        {{
                            drugSafeUser.web_user_object
                                ? `${drugSafeUser.web_user_object.first_name} ${drugSafeUser.web_user_object.last_name} (${drugSafeUser.web_user_object.email})`
                                : ''
                        }}
                    </p>
                </div>
            </div>
            <!-- MODAL BUTTONS (OK, CANCEL) -->
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="submit">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleConfirmationCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import DrugSafeUserFormFields from './DrugSafeUserFormFields';
import { getDrugSafeUser, handleAxiosError, updateDrugSafeUser } from '@/services/axiosService';

export default {
    components: { DrugSafeUserFormFields, Loading },
    data() {
        return {
            drugSafeUser: {
                first_name: null,
                last_name: null,
                id: null,
                pharmacy_id: this.$route.params.id,
                pincode: null,
                pincode_confirmation: null,
                role: null,
                web_user: null,
                web_user_object: null
            },
            drugSafeUserOriginal: {},
            error: false,
            from: '',
            loaded: {
                container: false,
                form: false
            },
            submitClicked: false
        };
    },
    // save previous page name for routing
    beforeRouteEnter(_to, from, next) {
        next(vm => {
            vm.from = from.name;
        });
        next();
    },
    async created() {
        // create drugsafe user and set container loaded
        await this.getDrugSafeUser();
        this.setLoaded('container');
    },
    computed: {
        ...mapState('account', ['user'])
    },
    methods: {
        ...mapActions('account', ['updateProfile']),
        // get drugsafe user based on route params
        async getDrugSafeUser() {
            try {
                const user = await getDrugSafeUser(this.$route.params.id, this.$route.params.second_id);
                this.setUser(user);
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // handle confirmation modal cancel action
        handleConfirmationCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('submitConfirmationModal');
        },
        // handle submit click
        async handleSubmitClick() {
            this.submitClicked = true;
            if (this.drugSafeUser.web_user && this.drugSafeUser.web_user !== this.drugSafeUserOriginal.web_user) {
                // if drugsafe user is being associated with a new web user, show confirmation modal
                this.$bvModal.show('submitConfirmationModal');
            } else {
                // otherwise submit directly
                await this.submit();
            }
        },
        // return to previous page (if returning to user index, retain previous tab selection)
        returnToPreviousPage() {
            if (this.from === 'user.index') {
                this.$router.push({ name: this.from, params: { tab: 'drugSafe' } });
            } else {
                this.$router.go(-1);
            }
        },
        // set component loaded
        setLoaded(component) {
            if (!this.error) {
                this.loaded[component] = true;
            }
        },
        // set user in variable
        setUser(user) {
            this.drugSafeUserOriginal = user;
            this.drugSafeUser = { ...this.drugSafeUser, ...user };
        },
        // submit form and return to previous page
        async submit() {
            try {
                this.$bvModal.hide('submitConfirmationModal');
                delete this.drugSafeUser.web_user_object;
                await updateDrugSafeUser(this.$route.params.id, this.$route.params.second_id, this.drugSafeUser);
                this.$toastr.s(this.$t('drugsafe_user_updated'));
                if (
                    this.user.id === this.drugSafeUser.web_user ||
                    this.user.id === this.drugSafeUserOriginal.web_user
                ) {
                    // if current user was associated with or disassociated from drugsafe user, update user profile
                    await this.updateProfile();
                }
                this.returnToPreviousPage();
            } catch (error) {
                handleAxiosError(error);
                if (error.response.data.status === 409) {
                    // if backend returns 409 (= pin not unique), set appropriate form field error
                    this.$refs.editSafeUserForm.setErrors({ drugSafeUserPin: ['error_pin_not_unique'] });
                }
                this.submitClicked = false;
            }
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
